import { Graph } from '@vromo/api';
import uuid from 'uuid';
import {
    AcceptedFleetOptions,
    FleetOption,
    FleetRequest,
    RestaurantType,
    SitesRequestDetails
} from './interfaces';

export const getNewRestaurant = () => ({
    id: uuid(),
    name: '',
    zipCodes: '',
    fleetOptions: []
});

export const getAcceptedFleetNames = (
    restaurantId: string,
    acceptedFleetOptions: AcceptedFleetOptions[]
) =>
    acceptedFleetOptions?.find(
        (fleetOptions: AcceptedFleetOptions) => fleetOptions.restaurantId === restaurantId
    )?.acceptedFleetNames || [];

export const getCoveredZipCodesForRestaurant = (
    restaurant: RestaurantType,
    acceptedFleetOptions: AcceptedFleetOptions[]
) => {
    const fleetsForRestaurant = getAcceptedFleetNames(restaurant.id, acceptedFleetOptions);
    if (!fleetsForRestaurant) return [];

    let zipcodes: string[] = [];
    restaurant.fleetOptions?.forEach(
        (result) =>
            fleetsForRestaurant.includes(result.name) &&
            (zipcodes = [...zipcodes, ...result.zipCodeCoverage])
    );
    return [...new Set(zipcodes)]; // duplicates
};

export const fetchAvailability = async (
    graph: Graph,
    roleId: number | null,
    restaurants: RestaurantType[],
    editedRestaurants: string[],
    acceptedFleetOptions: AcceptedFleetOptions[],
    updateRestaurant: (restaurant: RestaurantType) => void
) => {
    let newAcceptedOptions = acceptedFleetOptions;
    await Promise.all(
        restaurants.map(async ({ id, name, zipCodes }) => {
            if (editedRestaurants.includes(id)) {
                // graph.root.graph().roles(role.id).jobs().post(postData);

                let zipCodesToSubmit = zipCodes
                    .replace(/ /g, ',')
                    .replace(/\n/g, ',')
                    .replace(/;/g, ',')
                    .split(',');
                zipCodesToSubmit = zipCodesToSubmit.filter((item) => item !== '');

                const zipCodeString = Array.from(new Set(zipCodesToSubmit)).reduce(
                    (previousValue, currentValue, index) =>
                        previousValue + (index === 0 ? currentValue : `, ${currentValue}`),
                    ''
                );

                const trimmedName = name.trim();
                const updatedRestaurant = { id, name: trimmedName, zipCodes: zipCodeString };

                updateRestaurant({
                    ...updatedRestaurant,
                    fleetOptions: []
                });

                if (
                    !zipCodesToSubmit ||
                    zipCodesToSubmit.length == 0 ||
                    (zipCodesToSubmit.length == 1 && zipCodesToSubmit[0]?.length == 0)
                ) {
                    return null;
                }

                const response = await graph.root
                    .graph()
                    .roles(roleId)
                    .deliveryServiceProviders()
                    .availabilityForFleets()
                    .post({ zipCodes: zipCodesToSubmit });

                if (response && response?._embedded) {
                    const fleetOptions: FleetOption[] =
                        response._embedded?.deliveryServiceProviders;

                    updateRestaurant({
                        ...updatedRestaurant,
                        fleetOptions
                    });

                    const acceptedFleetNames = fleetOptions.map((fleetOption) => fleetOption.name);

                    newAcceptedOptions = newAcceptedOptions.filter((o) => o.restaurantId !== id);
                    newAcceptedOptions.push({ restaurantId: id, acceptedFleetNames });
                }
            }
        })
    );
    return newAcceptedOptions;
};

export const submitForm = async (
    graph: Graph,
    roleId: number | null,
    restaurants: RestaurantType[],
    contactEmail: string,
    existingProviders: string[],
    acceptedFleetOptions: AcceptedFleetOptions[]
) => {
    const dataToSubmit: FleetRequest = {
        deliveryServiceProviderActivationRequest: {
            contactEmail,
            sitesRequestDetails: [],
            existingProviders
        }
    };
    restaurants.map((restaurant: RestaurantType) => {
        const acceptedOptionsForRestaurant = acceptedFleetOptions.find(
            ({ restaurantId }) => restaurantId === restaurant.id
        );

        const coveredZipCodes = getCoveredZipCodesForRestaurant(restaurant, acceptedFleetOptions);

        const details: SitesRequestDetails = {
            siteName: restaurant.name,
            deliveryServiceProviderNames: acceptedOptionsForRestaurant?.acceptedFleetNames || [],
            coveredZipCodes,
            uncoveredZipCodes: restaurant.zipCodes
                .split(', ')
                .filter((zipCode: string) => !coveredZipCodes.includes(zipCode))
        };
        dataToSubmit.deliveryServiceProviderActivationRequest.sitesRequestDetails.push(details);
    });

    const response = await graph.root
        .graph()
        .roles(roleId)
        .deliveryServiceProviders()
        .deliveryServiceProviderActivation()
        .post(dataToSubmit);

    return response;
};
